import { FaVoteYea, FaPlus, FaCheckCircle, FaClock, FaLock, FaUserShield, FaEdit, FaTrash, FaEye, FaSignOutAlt, FaChartBar } from "react-icons/fa";
import { useState } from "react";

export default function Votes() {
  // Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Mock user database (in production, this would be in a backend)
  const users = {
    "admin@staffcouncil.nyu.edu": { 
      password: "admin123", 
      name: "Admin User", 
      role: "admin",
      id: "admin-1"
    },
    "js9640@nyu.edu": { 
      password: "member123", 
      name: "Josie Simm", 
      role: "member",
      id: "member-1"
    },
    "mwc347@nyu.edu": { 
      password: "member123", 
      name: "Mohammed Waseem Chaudry", 
      role: "member",
      id: "member-2"
    },
    "kjt9854@nyu.edu": { 
      password: "member123", 
      name: "Karol Tolpa", 
      role: "member",
      id: "member-3"
    },
  };

  // Voting state
  const [polls, setPolls] = useState([
    {
      id: 1,
      title: "Approve New Budget Allocation",
      description: "Vote on the proposed budget allocation for staff development programs in Q1 2025.",
      options: ["Approve", "Reject", "Abstain"],
      endDate: "2025-01-25",
      status: "active",
      createdBy: "admin-1",
      // Anonymous votes: stored separately from voter identity
      votes: {
        "Approve": 5,
        "Reject": 2,
        "Abstain": 3
      },
      // Track who voted (NOT what they voted for)
      voterIds: ["member-1", "member-2", "member-3"]
    },
    {
      id: 2,
      title: "Change Meeting Schedule",
      description: "Should we move monthly meetings from Wednesdays to Thursdays?",
      options: ["Yes", "No"],
      endDate: "2025-01-20",
      status: "active",
      createdBy: "admin-1",
      votes: {
        "Yes": 4,
        "No": 3
      },
      voterIds: ["member-2", "member-3"]
    },
    {
      id: 3,
      title: "Approve Event Date",
      description: "Vote on the proposed date for the annual staff appreciation event.",
      options: ["March 15", "March 22", "March 29"],
      endDate: "2025-01-10",
      status: "closed",
      createdBy: "admin-1",
      votes: {
        "March 15": 4,
        "March 22": 8,
        "March 29": 3
      },
      voterIds: ["member-1", "member-2", "member-3"]
    },
  ]);

  const [selectedVote, setSelectedVote] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showNewPollForm, setShowNewPollForm] = useState(false);
  const [showResults, setShowResults] = useState(null);

  // New poll form state
  const [newPoll, setNewPoll] = useState({
    title: "",
    description: "",
    options: ["", ""],
    endDate: "",
  });

  // Login handler
  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError("");
    
    const user = users[loginEmail.toLowerCase()];
    if (user && user.password === loginPassword) {
      setCurrentUser({
        email: loginEmail.toLowerCase(),
        name: user.name,
        role: user.role,
        id: user.id
      });
      setLoginPassword("");
    } else {
      setLoginError("Invalid email or password");
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setShowAdminPanel(false);
    setLoginEmail("");
    setLoginPassword("");
  };

  // Cast vote (anonymous)
  const castVote = (pollId) => {
    if (!selectedOption || !currentUser) return;
    
    setPolls(polls.map(poll => {
      if (poll.id === pollId) {
        return {
          ...poll,
          votes: {
            ...poll.votes,
            [selectedOption]: (poll.votes[selectedOption] || 0) + 1
          },
          voterIds: [...poll.voterIds, currentUser.id]
        };
      }
      return poll;
    }));
    
    setSelectedVote(null);
    setSelectedOption(null);
    alert("Your anonymous vote has been recorded!");
  };

  // Admin: Create new poll
  const createPoll = () => {
    if (!newPoll.title || !newPoll.description || newPoll.options.filter(o => o).length < 2 || !newPoll.endDate) {
      alert("Please fill in all fields and provide at least 2 options");
      return;
    }

    const poll = {
      id: polls.length + 1,
      title: newPoll.title,
      description: newPoll.description,
      options: newPoll.options.filter(o => o),
      endDate: newPoll.endDate,
      status: "active",
      createdBy: currentUser.id,
      votes: {},
      voterIds: []
    };

    // Initialize vote counts
    poll.options.forEach(option => {
      poll.votes[option] = 0;
    });

    setPolls([poll, ...polls]);
    setNewPoll({ title: "", description: "", options: ["", ""], endDate: "" });
    setShowNewPollForm(false);
    alert("Poll created successfully!");
  };

  // Admin: Delete poll
  const deletePoll = (pollId) => {
    if (window.confirm("Are you sure you want to delete this poll?")) {
      setPolls(polls.filter(p => p.id !== pollId));
    }
  };

  // Admin: Close poll
  const closePoll = (pollId) => {
    setPolls(polls.map(p => 
      p.id === pollId ? { ...p, status: "closed" } : p
    ));
  };

  const hasUserVoted = (poll) => {
    return currentUser && poll.voterIds.includes(currentUser.id);
  };

  const getTotalVotes = (poll) => {
    return Object.values(poll.votes).reduce((sum, count) => sum + count, 0);
  };

  // Login Screen
  if (!currentUser) {
    return (
      <div style={{
        padding: "40px",
        maxWidth: "500px",
        margin: "100px auto",
      }}>
        <div style={{
          background: "white",
          borderRadius: "16px",
          padding: "40px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          border: "1px solid #e2e8f0",
        }}>
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
            <div style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px",
            }}>
              <FaLock style={{ fontSize: "24px", color: "white" }} />
            </div>
            <h1 style={{
              fontSize: "28px",
              fontWeight: "700",
              color: "#2d3748",
              marginBottom: "8px",
            }}>
              Staff Council Voting
            </h1>
            <p style={{ color: "#718096", fontSize: "15px" }}>
              Sign in to participate in anonymous voting
            </p>
          </div>

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: "20px" }}>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#2d3748",
                marginBottom: "8px",
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="your.email@nyu.edu"
                required
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "15px",
                }}
              />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#2d3748",
                marginBottom: "8px",
              }}>
                Password
              </label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Enter your password"
                required
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "15px",
                }}
              />
            </div>

            {loginError && (
              <div style={{
                padding: "12px",
                background: "#fee",
                border: "1px solid #c53030",
                borderRadius: "8px",
                color: "#c53030",
                fontSize: "14px",
                marginBottom: "20px",
              }}>
                {loginError}
              </div>
            )}

            <button
              type="submit"
              style={{
                width: "100%",
                padding: "14px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 4px 6px rgba(102, 126, 234, 0.3)",
              }}
            >
              Sign In
            </button>
          </form>

          <div style={{
            marginTop: "24px",
            padding: "16px",
            background: "#f7fafc",
            borderRadius: "8px",
            fontSize: "13px",
            color: "#4a5568",
          }}>
            <strong>Demo Credentials:</strong>
            <div style={{ marginTop: "8px" }}>
              <div><strong>Admin:</strong> admin@staffcouncil.nyu.edu / admin123</div>
              <div><strong>Member:</strong> js9640@nyu.edu / member123</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Panel
  if (showAdminPanel && currentUser.role === "admin") {
    return (
      <div style={{ padding: "40px", maxWidth: "1200px", margin: "0 auto" }}>
        {/* Admin Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "40px",
        }}>
          <div>
            <h1 style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "#2d3748",
              marginBottom: "8px",
            }}>
              <FaUserShield style={{ marginRight: "12px", color: "#667eea" }} />
              Admin Panel
            </h1>
            <p style={{ color: "#718096", fontSize: "16px" }}>
              Manage polls and view anonymous results
            </p>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={() => setShowAdminPanel(false)}
              style={{
                padding: "12px 24px",
                background: "#e2e8f0",
                color: "#2d3748",
                border: "none",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Back to Voting
            </button>
            <button
              onClick={handleLogout}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 24px",
                background: "#718096",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              <FaSignOutAlt />
              Logout
            </button>
          </div>
        </div>

        {/* Create New Poll Button */}
        {!showNewPollForm && (
          <button
            onClick={() => setShowNewPollForm(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "14px 28px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer",
              marginBottom: "32px",
              boxShadow: "0 4px 6px rgba(102, 126, 234, 0.3)",
            }}
          >
            <FaPlus />
            Create New Poll
          </button>
        )}

        {/* New Poll Form */}
        {showNewPollForm && (
          <div style={{
            background: "white",
            borderRadius: "12px",
            padding: "32px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e2e8f0",
            marginBottom: "32px",
          }}>
            <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "24px", color: "#2d3748" }}>
              Create New Poll
            </h2>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                Poll Title *
              </label>
              <input
                type="text"
                value={newPoll.title}
                onChange={(e) => setNewPoll({ ...newPoll, title: e.target.value })}
                placeholder="Enter poll title"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "15px",
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                Description *
              </label>
              <textarea
                value={newPoll.description}
                onChange={(e) => setNewPoll({ ...newPoll, description: e.target.value })}
                placeholder="Describe what this vote is about"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "15px",
                  minHeight: "100px",
                  resize: "vertical",
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                Voting Options (minimum 2) *
              </label>
              {newPoll.options.map((option, index) => (
                <div key={index} style={{ display: "flex", gap: "12px", marginBottom: "8px" }}>
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...newPoll.options];
                      newOptions[index] = e.target.value;
                      setNewPoll({ ...newPoll, options: newOptions });
                    }}
                    placeholder={`Option ${index + 1}`}
                    style={{
                      flex: 1,
                      padding: "10px 14px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "15px",
                    }}
                  />
                  {index > 1 && (
                    <button
                      onClick={() => {
                        const newOptions = newPoll.options.filter((_, i) => i !== index);
                        setNewPoll({ ...newPoll, options: newOptions });
                      }}
                      style={{
                        padding: "10px 16px",
                        background: "#fee",
                        color: "#c53030",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setNewPoll({ ...newPoll, options: [...newPoll.options, ""] })}
                style={{
                  padding: "8px 16px",
                  background: "#f7fafc",
                  color: "#4a5568",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  marginTop: "8px",
                }}
              >
                + Add Option
              </button>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                End Date *
              </label>
              <input
                type="date"
                value={newPoll.endDate}
                onChange={(e) => setNewPoll({ ...newPoll, endDate: e.target.value })}
                style={{
                  padding: "12px 16px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "15px",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={createPoll}
                style={{
                  padding: "12px 32px",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Create Poll
              </button>
              <button
                onClick={() => {
                  setShowNewPollForm(false);
                  setNewPoll({ title: "", description: "", options: ["", ""], endDate: "" });
                }}
                style={{
                  padding: "12px 32px",
                  background: "#e2e8f0",
                  color: "#2d3748",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Poll Management List */}
        <div>
          <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "20px", color: "#2d3748" }}>
            All Polls
          </h2>
          {polls.map(poll => (
            <div key={poll.id} style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
                marginBottom: "16px",
              }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                    {poll.title}
                  </h3>
                  <p style={{ color: "#4a5568", fontSize: "15px", marginBottom: "12px" }}>
                    {poll.description}
                  </p>
                  <div style={{ display: "flex", gap: "16px", fontSize: "13px", color: "#718096" }}>
                    <span>Status: <strong style={{ color: poll.status === "active" ? "#38a169" : "#718096" }}>{poll.status}</strong></span>
                    <span>Ends: <strong>{new Date(poll.endDate).toLocaleDateString()}</strong></span>
                    <span>Total Votes: <strong>{getTotalVotes(poll)}</strong></span>
                    <span>Voters: <strong>{poll.voterIds.length}</strong></span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => setShowResults(showResults === poll.id ? null : poll.id)}
                    style={{
                      padding: "8px 12px",
                      background: "#667eea",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <FaChartBar />
                    {showResults === poll.id ? "Hide" : "View"} Results
                  </button>
                  {poll.status === "active" && (
                    <button
                      onClick={() => closePoll(poll.id)}
                      style={{
                        padding: "8px 12px",
                        background: "#d97706",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      Close Poll
                    </button>
                  )}
                  <button
                    onClick={() => deletePoll(poll.id)}
                    style={{
                      padding: "8px 12px",
                      background: "#c53030",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              {/* Anonymous Results */}
              {showResults === poll.id && (
                <div style={{
                  marginTop: "20px",
                  padding: "20px",
                  background: "#f7fafc",
                  borderRadius: "8px",
                }}>
                  <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#2d3748", marginBottom: "16px" }}>
                    📊 Anonymous Results
                  </h4>
                  <div style={{ fontSize: "13px", color: "#718096", marginBottom: "16px", fontStyle: "italic" }}>
                    ⚠️ Results are anonymous. You can see vote counts but NOT who voted for what.
                  </div>
                  {poll.options.map(option => {
                    const voteCount = poll.votes[option] || 0;
                    const totalVotes = getTotalVotes(poll);
                    const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                    return (
                      <div key={option} style={{ marginBottom: "12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                          <span style={{ fontSize: "15px", fontWeight: "500", color: "#2d3748" }}>{option}</span>
                          <span style={{ fontSize: "15px", fontWeight: "600", color: "#667eea" }}>
                            {voteCount} votes ({percentage}%)
                          </span>
                        </div>
                        <div style={{ height: "10px", background: "#e2e8f0", borderRadius: "5px", overflow: "hidden" }}>
                          <div
                            style={{
                              height: "100%",
                              width: `${percentage}%`,
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              transition: "width 0.3s",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Member Voting View
  const activePolls = polls.filter(p => p.status === "active");
  const closedPolls = polls.filter(p => p.status === "closed");

  return (
    <div style={{ padding: "40px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "40px",
      }}>
        <div>
          <h1 style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#2d3748",
            marginBottom: "8px",
          }}>
            Internal Votes
          </h1>
          <p style={{ color: "#718096", fontSize: "16px" }}>
            Welcome, <strong>{currentUser.name}</strong> — All votes are anonymous
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {currentUser.role === "admin" && (
            <button
              onClick={() => setShowAdminPanel(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 24px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: "500",
                cursor: "pointer",
                boxShadow: "0 4px 6px rgba(102, 126, 234, 0.3)",
              }}
            >
              <FaUserShield />
              Admin Panel
            </button>
          )}
          <button
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 24px",
              background: "#718096",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            <FaSignOutAlt />
            Logout
          </button>
        </div>
      </div>

      {/* Active Votes */}
      <div style={{ marginBottom: "40px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "600", color: "#2d3748", marginBottom: "20px" }}>
          Active Votes
        </h2>
        {activePolls.length > 0 ? (
          activePolls.map(poll => {
            const hasVoted = hasUserVoted(poll);
            const totalVotes = getTotalVotes(poll);

            return (
              <div
                key={poll.id}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #e2e8f0",
                  marginBottom: "16px",
                }}
              >
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  marginBottom: "16px",
                }}>
                  <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", margin: 0 }}>
                    {poll.title}
                  </h3>
                  <span style={{
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "500",
                    background: "#e6f7ee",
                    color: "#38a169",
                  }}>
                    Active
                  </span>
                </div>

                <p style={{
                  color: "#4a5568",
                  fontSize: "15px",
                  lineHeight: "1.6",
                  marginBottom: "16px",
                }}>
                  {poll.description}
                </p>

                {!hasVoted ? (
                  <div style={{ marginBottom: "16px" }}>
                    {poll.options.map(option => (
                      <button
                        key={option}
                        onClick={() => {
                          setSelectedVote(poll.id);
                          setSelectedOption(option);
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "12px 16px",
                          marginBottom: "8px",
                          background: selectedVote === poll.id && selectedOption === option ? "#667eea" : "#f7fafc",
                          color: selectedVote === poll.id && selectedOption === option ? "white" : "#2d3748",
                          border: selectedVote === poll.id && selectedOption === option ? "2px solid #667eea" : "2px solid #e2e8f0",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "15px",
                          fontWeight: "500",
                          textAlign: "left",
                          transition: "all 0.2s",
                        }}
                      >
                        {option}
                      </button>
                    ))}
                    {selectedVote === poll.id && (
                      <button
                        onClick={() => castVote(poll.id)}
                        style={{
                          marginTop: "8px",
                          padding: "12px 24px",
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontWeight: "600",
                          fontSize: "15px",
                          boxShadow: "0 4px 6px rgba(102, 126, 234, 0.3)",
                        }}
                      >
                        Submit Vote (Anonymous)
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "12px 16px",
                    background: "#e6f7ee",
                    borderRadius: "8px",
                    color: "#38a169",
                    marginBottom: "16px",
                  }}>
                    <FaCheckCircle />
                    <span style={{ fontWeight: "500" }}>You have already voted</span>
                  </div>
                )}

                <div style={{ display: "flex", gap: "20px", fontSize: "13px", color: "#718096" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <FaClock />
                    <span>Ends: {new Date(poll.endDate).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <strong>{totalVotes}</strong> total votes
                  </div>
                  <div>
                    <strong>{poll.voterIds.length}</strong> members participated
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p style={{ color: "#718096", fontStyle: "italic" }}>No active votes at this time</p>
        )}
      </div>

      {/* Closed Votes (with results) */}
      <div>
        <h2 style={{ fontSize: "24px", fontWeight: "600", color: "#2d3748", marginBottom: "20px" }}>
          Past Votes
        </h2>
        {closedPolls.length > 0 ? (
          closedPolls.map(poll => {
            const totalVotes = getTotalVotes(poll);
            return (
              <div
                key={poll.id}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #e2e8f0",
                  marginBottom: "16px",
                }}
              >
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  marginBottom: "16px",
                }}>
                  <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", margin: 0 }}>
                    {poll.title}
                  </h3>
                  <span style={{
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "500",
                    background: "#f0f0f0",
                    color: "#718096",
                  }}>
                    Closed
                  </span>
                </div>

                <p style={{
                  color: "#4a5568",
                  fontSize: "15px",
                  lineHeight: "1.6",
                  marginBottom: "16px",
                }}>
                  {poll.description}
                </p>

                {/* Results */}
                <div style={{ marginBottom: "16px" }}>
                  <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#718096", marginBottom: "12px" }}>
                    Results:
                  </h4>
                  {poll.options.map(option => {
                    const voteCount = poll.votes[option] || 0;
                    const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                    return (
                      <div key={option} style={{ marginBottom: "10px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                          <span style={{ fontSize: "14px", color: "#4a5568" }}>{option}</span>
                          <span style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                            {voteCount} votes ({percentage}%)
                          </span>
                        </div>
                        <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                          <div
                            style={{
                              height: "100%",
                              width: `${percentage}%`,
                              background: "#667eea",
                              transition: "width 0.3s",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: "flex", gap: "20px", fontSize: "13px", color: "#718096" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <FaClock />
                    <span>Ended: {new Date(poll.endDate).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <strong>{totalVotes}</strong> total votes
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p style={{ color: "#718096", fontStyle: "italic" }}>No past votes</p>
        )}
      </div>
    </div>
  );
}
